import { navigateToAndResetPageAndSearch } from 'front-end/src/pages/analytics/helpers'
import React, { FC, useContext, useState } from 'react'
import { Box } from '@chakra-ui/react'
import { Column } from 'react-table'
import { Trans, t } from '@lingui/macro'

import {
  ResourcesOverviewOrderColumn,
  SqlOrderDirectionEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { formatDateAndTimeShort } from 'front-end/src/utils/dateHelpers'

import TableExtendedOld, {
  DEFAULT_TR_PROPS
} from '../../../components/common/table-extended-old/TableExtendedOld'
import UserAvatar from '../../../components/common/user/user-avatar/UserAvatar'
import { IBaseOverviewFilter } from '../interfaces'
import { linkBuilder } from 'shared/linkBuilder'
import { DownloadResourcesCSV } from './DownloadResourcesCSV'
import { TopicSmallMarbleRating } from '../../../components/common/marble-rating/TopicSmallMarbleRating'
import { useResourcesOverviewTableQuery } from './ResourcesAnalytics.codegen'
import { AnalyticsContext } from '../AnalyticsContext'
import {
  StatusArchivedGrey,
  StatusDraftGrey
} from 'front-end/src/components/common/status/Status'
import { EllipsisWithTippy } from '../EllipsisWithTooltip'

export interface IOverviewFilter extends IBaseOverviewFilter {
  orderColumn: ResourcesOverviewOrderColumn
}

const tableColumns: Array<Column<any>> = [
  {
    Header: <Trans>Name</Trans>,
    id: ResourcesOverviewOrderColumn.resourceName,
    accessor: 'topic.currentTitle',
    Cell: (row) => (
      <EllipsisWithTippy tippyText={row.value}>
        <strong className="dark">{row.value}</strong>
        {row.original.topic.is_archived && <StatusArchivedGrey />}
        {!row.original.topic.is_archived &&
          !row.original.topic.production_topic_version_id && (
            <StatusDraftGrey mx="8px" />
          )}
      </EllipsisWithTippy>
    )
  },
  {
    Header: <Trans>Workspace</Trans>,
    id: ResourcesOverviewOrderColumn.workspaceName,
    maxWidth: 220,
    accessor: 'topic.workspace.name',
    Cell: (row) => <Box className="overflowEllipsis">{row.value}</Box>
  },
  {
    Header: <Trans>Author</Trans>,
    id: ResourcesOverviewOrderColumn.displayName,
    accessor: 'topic.author',
    maxWidth: 200,
    Cell: (row) => (
      <div className="AnalyticsTable__user overflowEllipsis">
        <UserAvatar size="small" user={row.value} />
        {row.value.display_name}
      </div>
    )
  },
  {
    Header: <Trans>Avg. Rating</Trans>,
    id: ResourcesOverviewOrderColumn.averageRating,
    maxWidth: 150,
    className: 'overflowVisible',
    accessor: 'topic',
    Cell: (row) =>
      row.value.total_rating > 0 && (
        <TopicSmallMarbleRating
          topicId={row.value.id}
          numberRated={row.value.number_rated}
          rating={row.value.averageRating}
        />
      )
  },
  {
    Header: <Trans>Pass rate</Trans>,
    id: ResourcesOverviewOrderColumn.passRate,
    maxWidth: 100,
    accessor: 'passRate',
    Cell: (row) => <>{row.value}%</>
  },
  {
    Header: <Trans>Views</Trans>,
    id: ResourcesOverviewOrderColumn.viewsCount,
    maxWidth: 100,
    accessor: 'viewsCount',
    Cell: (row) => <span className="green">{row.value}</span>
  },
  {
    Header: <Trans>Last viewed</Trans>,
    id: ResourcesOverviewOrderColumn.lastViewedAt,
    maxWidth: 200,
    accessor: 'topic.lastViewedAt',
    Cell: (props) => props.value && formatDateAndTimeShort(props.value)
  }
]

const ResourcesOverviewTable: FC<{}> = () => {
  const { filterAsQueryVariables, filter, changeFilter } =
    useContext(AnalyticsContext)
  const [resourcesTableFilters, setResourcesTableFilters] =
    useState<IOverviewFilter>({
      limit: 20,
      orderColumn: ResourcesOverviewOrderColumn.viewsCount,
      orderDirection: SqlOrderDirectionEnum.DESC
    })

  const overviewTableQuery = {
    ...resourcesTableFilters,
    page: filter.page,
    search: filter.search
  }

  const { data, loading } = useResourcesOverviewTableQuery({
    variables: {
      mainAnalyticsFilter: filterAsQueryVariables,
      resourcesOverviewFilter: overviewTableQuery
    }
  })

  let pagesCount
  if (
    data &&
    data.analytics &&
    typeof data.analytics.resources.resourcesCount === 'number'
  ) {
    pagesCount = Math.ceil(
      data.analytics.resources.resourcesCount / overviewTableQuery.limit
    )
  } else {
    pagesCount = 1
  }

  return (
    <div>
      <TableExtendedOld
        loading={loading}
        manual
        data={data?.analytics.resources.resourcesOverview}
        tableHeader={t`Resources`}
        columns={tableColumns}
        className="-highlight AnalyticsTable"
        defaultPageSize={overviewTableQuery.limit}
        showPageSizeOptions={false}
        showSearchFilter
        resizable={false}
        minRows={1}
        pages={pagesCount}
        getTrProps={(_state, row) => ({
          onClick: () => {
            navigateToAndResetPageAndSearch(
              linkBuilder.analyticsResourceDetail(row.original.topic.id),
              changeFilter
            )
          },
          className: `clickable ${
            row && row.original.topic.deleted_at ? 'archived' : ''
          }`,
          ...DEFAULT_TR_PROPS
        })}
        paginationContentAfter={
          data && (
            <DownloadResourcesCSV
              resourcesOverviewFilter={overviewTableQuery}
              resourcesCount={data.analytics.resources.resourcesCount}
            />
          )
        }
        defaultSortDesc
        defaultSorted={[
          {
            id: overviewTableQuery.orderColumn,
            desc:
              overviewTableQuery.orderDirection === SqlOrderDirectionEnum.DESC
          }
        ]}
        onSortedChange={(newSorted) => {
          setResourcesTableFilters((prevState) => {
            const descendingOrder = newSorted[0] ? newSorted[0].desc : false

            return {
              ...prevState,
              orderColumn: newSorted[0]
                ? (newSorted[0].id as ResourcesOverviewOrderColumn)
                : ResourcesOverviewOrderColumn.resourceName,
              orderDirection: descendingOrder
                ? SqlOrderDirectionEnum.DESC
                : SqlOrderDirectionEnum.ASC
            }
          })
        }}
        onPageChange={(newPage) => {
          changeFilter({ page: newPage + 1 })
        }}
        defaultSearch={overviewTableQuery.search}
        page={overviewTableQuery.page - 1}
        onSearchChange={(searchVal) => {
          changeFilter({ search: searchVal })
        }}
      />
    </div>
  )
}

export default ResourcesOverviewTable
